import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Kunaki {
  _id: string;
  userId?: string;
  password?: string;
}

export function SettingsKunaki() {
  const [kunaki, setKunaki] = useState<Kunaki | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingKunaki, setIsCreatingKunaki] = useState(false);
  const [isDeletingKunaki, setIsDeletingKunaki] = useState(false);
  const [isSavingKunaki, setIsSavingKunaki] = useState(false);
  const [kunakiUserId, setKunakiUserId] = useState("");
  const [kunakiPassword, setKunakiPassword] = useState("");

  useEffect(() => {
    async function fetchKunaki() {
      setIsLoading(true);
      try {
        const kunakiResponse = await fetch("/api/kunaki");
        if (kunakiResponse.ok) {
          const kunakiData = await kunakiResponse.json();
          if (kunakiData.kunaki) {
            setKunaki(kunakiData.kunaki);
            setKunakiUserId(kunakiData.kunaki.userId || "");
            setKunakiPassword(kunakiData.kunaki.password || "");
          }
        } else if (kunakiResponse.status !== 404) {
          console.error("Failed to fetch Kunaki integration");
        }
      } catch (error) {
        console.error("Failed to fetch Kunaki integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchKunaki();
  }, []);

  const handleCreateKunaki = async () => {
    setIsCreatingKunaki(true);
    try {
      const response = await fetch("/api/kunaki", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.kunaki) {
          setKunaki(data.kunaki);
          setKunakiUserId(data.kunaki.userId || "");
          setKunakiPassword(data.kunaki.password || "");
          toast.success("Kunaki integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Kunaki integration");
      }
    } catch (error) {
      console.error("Error creating Kunaki integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Kunaki integration");
    } finally {
      setIsCreatingKunaki(false);
    }
  };

  const handleSaveKunaki = async () => {
    if (!kunaki) return;

    setIsSavingKunaki(true);
    try {
      const response = await fetch("/api/kunaki", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: kunakiUserId, password: kunakiPassword }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.kunaki) {
          setKunaki(data.kunaki);
          toast.success("Kunaki settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Kunaki settings");
      }
    } catch (error) {
      console.error("Error saving Kunaki settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Kunaki settings");
    } finally {
      setIsSavingKunaki(false);
    }
  };

  const handleDeleteKunaki = async () => {
    if (!kunaki) return;

    if (!confirm("Are you sure you want to delete your Kunaki integration?")) {
      return;
    }

    setIsDeletingKunaki(true);
    try {
      const response = await fetch(`/api/kunaki/${kunaki._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setKunaki(null);
        setKunakiUserId("");
        setKunakiPassword("");
        toast.success("Kunaki integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Kunaki integration");
      }
    } catch (error) {
      console.error("Error deleting Kunaki integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Kunaki integration");
    } finally {
      setIsDeletingKunaki(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Kunaki</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Kunaki account to manage print-on-demand products
            </p>
            {kunaki && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {!kunaki ? (
            <Button onClick={handleCreateKunaki} disabled={isCreatingKunaki}>
              {isCreatingKunaki ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          ) : (
            <Button onClick={handleDeleteKunaki} disabled={isDeletingKunaki} variant="destructive">
              {isDeletingKunaki ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        {kunaki && (
          <div className="space-y-2 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="kunakiUserId">User ID</Label>
              <Input
                id="kunakiUserId"
                type="text"
                value={kunakiUserId}
                onChange={(e) => setKunakiUserId(e.target.value)}
                placeholder="Enter Kunaki user ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kunakiPassword">Password</Label>
              <Input
                id="kunakiPassword"
                type="password"
                value={kunakiPassword}
                onChange={(e) => setKunakiPassword(e.target.value)}
                placeholder="Enter Kunaki password"
              />
            </div>
            <Button onClick={handleSaveKunaki} disabled={isSavingKunaki}>
              {isSavingKunaki ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
